import { Component, computed, effect, inject, input, model, output, signal } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { Dialog } from 'primeng/dialog';

import {
  AppointmentDetailsForUserResponse,
  AppointmentsClient,
  CalendarSlotDto,
  IntervalDto,
  RescheduleAppointmentCommand,
} from '../../model/fss.api';
import { CalendarComponent } from '../../ui/calendar/calendar';
import { ButtonComponent, ButtonSeverity, ButtonSize } from '../../ui/button/button';
import { FieldComponent } from '../../ui/field/field';
import { XClose } from '../../icons/x-close/x-close';
import { AlertService } from '../../services/alert.service';
import { UserService } from '../../services/user.service';

import { getDateFormatedSrShortWeekday } from '../../services/date.service';
import { slotCalendarDays } from '../../functions/slot-calendar';

@Component({
  standalone: true,
  selector: 'app-client-appointment-reschedule-dialog',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    Dialog,
    CalendarComponent,
    ButtonComponent,
    FieldComponent,
    XClose,
  ],
  templateUrl: './client-appointment-reschedule-dialog.html',
  styleUrl: './client-appointment-reschedule-dialog.scss',
})
export class ClientAppointmentRescheduleDialog {
  private readonly _appointments = inject(AppointmentsClient);
  private readonly _alert = inject(AlertService);
  private readonly _user = inject(UserService);

  visible = model.required<boolean>();
  appointmentId = input<string | undefined>(undefined);

  rescheduled = output<string>();

  protected readonly ButtonSeverity = ButtonSeverity;
  protected readonly ButtonSize = ButtonSize;

  details = signal<AppointmentDetailsForUserResponse | undefined>(undefined);
  calendarSlots = signal<CalendarSlotDto[]>([]);
  availableIntervals = signal<IntervalDto[]>([]);

  private readonly _calendarDays = computed(() => slotCalendarDays(this.calendarSlots()));
  minDate = computed(() => this._calendarDays().minDate);
  maxDate = computed(() => this._calendarDays().maxDate);
  disabledDates = computed(() => this._calendarDays().disabledDates);

  form: FormGroup = new FormGroup({
    date: new FormControl<Date | null>(null, { validators: [Validators.required] }),
    timeSlot: new FormControl<IntervalDto | null>(null, { validators: [Validators.required] }),
  });

  constructor() {
    this.form.controls['date']!.valueChanges.subscribe((date: Date | null) => {
      this.form.controls['timeSlot']!.setValue(null, { emitEvent: false });
      if (!date) {
        this.availableIntervals.set([]);
        return;
      }
      const key = this._dateKey(date);
      const slot = this.calendarSlots().find((s) => this._dateKey(s.dateTime!) === key);
      this.availableIntervals.set(slot?.intervals ?? []);
    });

    effect(() => {
      const id = this.appointmentId();
      const open = this.visible();
      if (open && id) {
        this._load(id);
      } else if (!open) {
        this._reset();
      }
    });
  }

  get dateCtrl(): FormControl {
    return this.form.controls['date'] as FormControl;
  }

  isIntervalSelected(interval: IntervalDto): boolean {
    const selected = this.form.controls['timeSlot']!.value as IntervalDto | null;
    return !!selected && selected.from === interval.from && selected.to === interval.to;
  }

  selectInterval(interval: IntervalDto): void {
    this.form.controls['timeSlot']!.setValue(interval);
  }

  formatTime(value: string | undefined): string {
    if (!value) return '';
    const match = value.match(/(\d{1,2}):(\d{2})/);
    if (!match) return value;
    return `${match[1]!.padStart(2, '0')}:${match[2]}`;
  }

  formatShortSrDate(date: Date): string {
    return getDateFormatedSrShortWeekday(date);
  }

  formatTimeRange(from?: Date, to?: Date): string {
    if (!from || !to) return '';
    const fmt = (d: Date) => d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
    return `${fmt(from)} - ${fmt(to)}`;
  }

  selectedTimeLabel(): string {
    const interval = this.form.controls['timeSlot']!.value as IntervalDto | null;
    if (!interval) return '';
    return `${this.formatTime(interval.from)} - ${this.formatTime(interval.to)}`;
  }

  onCancel(): void {
    this.visible.set(false);
  }

  async onSubmit(): Promise<void> {
    const id = this.appointmentId();
    if (!id || this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const date = this.form.controls['date']!.value as Date;
    const interval = this.form.controls['timeSlot']!.value as IntervalDto;
    const dateTime = this._combineDateAndTime(date, interval.from!);

    const command = new RescheduleAppointmentCommand({ id, dateTime });
    const res = await firstValueFrom(this._appointments.rescheduleAppointment(command));
    this._alert.addSuccessMsg(res.result!);
    this.rescheduled.emit(id);
    this.visible.set(false);
  }

  private async _load(id: string): Promise<void> {
    const res = await firstValueFrom(this._appointments.getAppointmentDetailsForUser(id));
    const d = res.result;
    this.details.set(d);

    const userId = this._user.getUserData.id;
    const duration = d?.duration;
    if (!userId || duration == null) return;

    const slotsRes = await firstValueFrom(this._appointments.getCalendarSlots(userId, duration, true));
    this.calendarSlots.set(slotsRes.result?.slots ?? []);
  }

  private _reset(): void {
    this.details.set(undefined);
    this.calendarSlots.set([]);
    this.availableIntervals.set([]);
    this.form.reset();
  }

  private _combineDateAndTime(date: Date, time: string): Date {
    const match = time.match(/(\d{1,2}):(\d{2})/);
    const hours = match ? Number(match[1]) : 0;
    const minutes = match ? Number(match[2]) : 0;
    const result = new Date(date);
    result.setHours(hours, minutes, 0, 0);
    return result;
  }

  private _dateKey(date: Date): string {
    return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
  }
}
